import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import useLazyInView from '../hooks/useLazyInView';
import {
  Box,
  Typography,
  Stack,
  Tooltip,
  Chip,
  IconButton,
  Dialog,
  Avatar,
  Divider,
  Button,
  Card,
  CardContent,
  Paper,
  Grow,
  CircularProgress,
} from '@mui/material';
import { useMediaQuery, useTheme, Fade } from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  Close,
  Login,
  Logout,
  Add as AddIcon,
  Remove as RemoveIcon,
  RestartAlt as RestartAltIcon,
  Search as SearchIcon,
  AssignmentInd as AssignmentIndIcon,
  Badge as BadgeIcon,
  PhotoLibrary as PhotoLibraryIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { formatDate, formatDateOnly } from '../utils/dateUtils';
import { peek as peekAuditImage, subscribe as subscribeAuditImage } from '../services/auditImageCache';
import useAuditImage from '../hooks/useAuditImage';
import { useInView } from 'react-intersection-observer';
import AuditImg from './AuditImg';
import { AuditRecordingPlayButton, isAuditRecordingReady } from './AuditRecordingPlayButton';
import { EmployeeNameHyperlink } from './EmployeeNameHyperlink';
import { buildPersonLinkMeta } from '../utils/personDetailsNavigation';
import { snapshotIsVendor, snapshotIsVisitor } from '../utils/employeeTypeUi';
import { useImagePreview } from './ImagePreviewDialog';

const ANONYMOUS_IMAGE = '/anonymous.webp';
const DEFAULT_PRIMARY = '#40518A';

const RoleBadgeIcons = ({ employeeType, size = 16 }) => (
  <>
    {snapshotIsVendor(employeeType) ? (
      <Tooltip title="Vendor" arrow>
        <AssignmentIndIcon sx={{ fontSize: size, color: DEFAULT_PRIMARY, flexShrink: 0 }} />
      </Tooltip>
    ) : null}
    {snapshotIsVisitor(employeeType) ? (
      <Tooltip title="Visitor" arrow>
        <BadgeIcon sx={{ fontSize: size, color: DEFAULT_PRIMARY, flexShrink: 0 }} />
      </Tooltip>
    ) : null}
  </>
);
const primary = DEFAULT_PRIMARY;
const RECORDING_PLAY_TRIGGER_SELECTOR = '[data-recording-play-trigger="true"]';
const RECORDING_CLICK_GUARD_UNTIL_KEY = '__recordingClickGuardUntil';

const isRecordingPlayInteraction = (event) => {
  const target = event?.target;
  if (target && typeof target.closest === 'function' && target.closest(RECORDING_PLAY_TRIGGER_SELECTOR)) {
    return true;
  }
  if (typeof window !== 'undefined') {
    const ignoreUntil = Number(window[RECORDING_CLICK_GUARD_UNTIL_KEY] || 0);
    if (Number.isFinite(ignoreUntil) && ignoreUntil > Date.now()) return true;
  }
  return false;
};

const getAuditImageKey = (evt) => {
  const raw = evt?.snapshot_audit_id || evt?.audit_id || evt?.recognition_audit_log?.audit_id || null;
  if (raw == null) return null;
  const key = String(raw).trim();
  return key || null;
};

/** All cache keys that can resolve the same event image (aliases must stay in sync). */
export const getAttendanceEventImageCacheKeys = (event) => {
  if (!event) return [];
  const keys = [];
  const add = (value) => {
    if (value == null) return;
    const key = String(value).trim();
    if (!key || keys.includes(key)) return;
    keys.push(key);
  };
  add(event.snapshot_audit_id);
  add(event.audit_id);
  add(event?.recognition_audit_log?.audit_id);
  if (event.image_available && event.attendance_id) {
    add(`attendance:${event.attendance_id}`);
  }
  return keys;
};

const isValidSnapshotImageUrl = (value) => typeof value === 'string' && value.length > 0;

const readCachedSnapshotImage = (snapshotCache, keys = []) => {
  if (!snapshotCache || !keys.length) return { status: 'missing' };
  let sawFailure = false;
  for (const key of keys) {
    if (!Object.prototype.hasOwnProperty.call(snapshotCache, key)) continue;
    const value = snapshotCache[key];
    if (isValidSnapshotImageUrl(value)) return { status: 'ready', value };
    if (value === null) sawFailure = true;
  }
  if (sawFailure) return { status: 'error' };
  return { status: 'missing' };
};

/** Merge snapshot cache writes without clobbering an already-loaded image URL. */
export const mergeAttendanceSnapshotCache = (prev, entries = {}) => {
  const next = { ...prev };
  let changed = false;
  Object.entries(entries).forEach(([key, value]) => {
    const normalizedKey = String(key).trim();
    if (!normalizedKey) return;
    if (isValidSnapshotImageUrl(next[normalizedKey])) return;
    if (next[normalizedKey] === value) return;
    next[normalizedKey] = value;
    changed = true;
  });
  return changed ? next : prev;
};

/** Same resolution path as the multi-image gallery — snapshot cache, session cache, then nested fields. */
const resolveAttendanceEventImage = (event, snapshotCache) => {
  if (!event) return undefined;
  const cacheKeys = getAttendanceEventImageCacheKeys(event);
  const cached = readCachedSnapshotImage(snapshotCache, cacheKeys);
  if (cached.status === 'ready') return cached.value;

  for (const key of cacheKeys) {
    if (!key || String(key).startsWith('attendance:')) continue;
    const sessionUrl = peekAuditImage(key);
    if (sessionUrl !== undefined && sessionUrl !== null) return sessionUrl;
  }

  const nestedImages = collectImageStringsFromRecord(event);
  if (nestedImages.length > 0) return nestedImages[0].src;

  if (cached.status === 'error') return null;
  return undefined;
};

function attendanceLogHasImagePath(evt) {
  if (!evt) return false;
  const path = evt.image_path;
  if (typeof path === 'string' && path.trim().length > 0) return true;
  if (getAuditImageKey(evt)) return true;
  return Boolean(evt.image_available);
}

/** Resolved snapshot URL when a stored image exists; otherwise anonymous placeholder. */
function resolveAttendanceLogDisplayUrl(evt, resolveCachedImageFn) {
  if (!attendanceLogHasImagePath(evt)) return ANONYMOUS_IMAGE;
  return resolveCachedImageFn(evt) || ANONYMOUS_IMAGE;
}

const isRenderableImageString = (value) => {
  if (typeof value !== 'string') return false;
  const trimmed = value.trim();
  if (!trimmed || trimmed === ANONYMOUS_IMAGE) return false;
  if (/^(data:image\/|blob:)/i.test(trimmed)) return true;
  if (/^https?:\/\//i.test(trimmed) && /\.(png|jpe?g|webp|gif|bmp|svg)(\?|#|$)/i.test(trimmed)) return true;
  return /^\/(?!anonymous\.webp)(.+\.)?(png|jpe?g|webp|gif|bmp|svg)(\?|#|$)/i.test(trimmed);
};

const formatImagePathLabel = (path = []) => {
  const raw = path[path.length - 1] || 'Image';
  return String(raw)
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const collectImageStringsFromRecord = (value, path = [], items = [], seenObjects = new WeakSet()) => {
  if (isRenderableImageString(value)) {
    const title = formatImagePathLabel(path);
    items.push({ src: value.trim(), title, alt: title });
    return items;
  }
  if (!value || typeof value !== 'object') return items;
  if (seenObjects.has(value)) return items;
  seenObjects.add(value);
  if (Array.isArray(value)) {
    value.forEach((item, index) => collectImageStringsFromRecord(item, [...path, index], items, seenObjects));
    return items;
  }
  Object.entries(value).forEach(([key, item]) => {
    collectImageStringsFromRecord(item, [...path, key], items, seenObjects);
  });
  return items;
};

/** Build distinct captured-frame gallery entries for attendance/intrusion summary cards. */
export const buildAttendanceCapturedImageGallery = ({ summary, snapshotCache }) => {
  const images = [];
  const seen = new Set();
  const addImage = (src, title, alt) => {
    if (!isRenderableImageString(src) || seen.has(src)) return;
    seen.add(src);
    images.push({ src, title: title || 'Captured frame', alt: alt || title || 'Captured frame' });
  };

  collectImageStringsFromRecord(summary).forEach((item) => addImage(item.src, item.title, item.alt));

  const events = Array.isArray(summary?.recent_events) ? summary.recent_events : [];
  events.forEach((event) => {
    const actionLabel = event?.action === 'check_in' ? 'Check In' : event?.action === 'check_out' ? 'Check Out' : 'Captured frame';
    const title = event?.created_at ? `${actionLabel} - ${formatDate(event.created_at, true, false)}` : actionLabel;
    const resolvedSrc = resolveAttendanceEventImage(event, snapshotCache);
    if (resolvedSrc) {
      addImage(resolvedSrc, title, actionLabel);
    }
    collectImageStringsFromRecord(event).forEach((item) => addImage(item.src, item.title, item.alt));
  });

  return images;
};

// Binance-style zoom/pan constants
const ZOOM_MIN_SPAN_MINS = 30;
const ZOOM_MAX_SPAN_MINS = (24 - 8) * 60; // full day
const ZOOM_WHEEL_FACTOR = 1.12;
const ZOOM_TRANSITION_MS = 150;

// Single Day Timeline Bar Component
export const DayTimelineBar = ({ date, events, isToday, snapshotCache, highlightEventKey = null, hideSummary = false, showInOutCounts = true, summaryDurationLabel = null, hideMarkers = false, onRequestMarkerImage = null }) => {
  const [clickedMarker, setClickedMarker] = useState(null);
  const [hoverInfo, setHoverInfo] = useState(null);
  const [dragHintHover, setDragHintHover] = useState(false);
  const [zoomState, setZoomState] = useState(null); // { visibleStartMinute, visibleEndMinute } or null = full day
  const [currentTime, setCurrentTime] = useState(new Date());
  const barRef = useRef(null);
  const barContainerRef = useRef(null);
  const [isPanning, setIsPanning] = useState(false);
  const panRef = useRef({ active: false, startX: 0, startStart: 0, startEnd: 0 });
  const pinchRef = useRef({ initialDistance: 0, initialSpan: 0, initialCenter: 0 });
  const lastWheelRef = useRef(0);
  const rafRef = useRef(null);
  const zoomStateRef = useRef(null);
  zoomStateRef.current = zoomState;

  // Update current time every 30 seconds if this is today (to keep presence time live)
  useEffect(() => {
    if (!isToday) return;
    
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 30000); // Update every 30 seconds
    
    return () => clearInterval(interval);
  }, [isToday]);
  
  const resolveCachedImage = (evt) => {
    if (!evt) return null;
    const auditId = getAuditImageKey(evt);
    if (auditId) {
      const sessionUrl = peekAuditImage(auditId);
      if (sessionUrl !== undefined && sessionUrl !== null) {
        return sessionUrl;
      }
      if (snapshotCache?.[auditId]) {
        return snapshotCache[auditId];
      }
    }
    if (evt.image_available) {
      const key = `attendance:${evt.attendance_id}`;
      return snapshotCache?.[key] || null;
    }
    return null;
  };

  // Timeline range: 08 AM to 12 AM (midnight) = 8:00 to 24:00
  const startHour = 8;
  const endHour = 24;
  const totalMinutes = (endHour - startHour) * 60; // 16 hours = 960 minutes

  // Helper to get hours and minutes in browser's timezone
  const getTimeInDisplayTimezone = (dateValue) => {
    let d = null;
    if (dateValue instanceof Date) {
      d = dateValue;
    } else if (typeof dateValue === 'string') {
      const trimmed = dateValue.trim();
      const utcString = trimmed.endsWith('Z') ? trimmed : trimmed + 'Z';
      d = new Date(utcString);
    } else {
      d = new Date(dateValue);
    }
    
    if (!d || isNaN(d.getTime())) return { hours: 0, minutes: 0 };
    
    // Get time parts in the browser's timezone (defaults to user's local timezone)
    const formatter = new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      hour12: false,
    });
    const parts = formatter.formatToParts(d);
    const hours = parseInt(parts.find(p => p.type === 'hour')?.value || '0', 10);
    const minutes = parseInt(parts.find(p => p.type === 'minute')?.value || '0', 10);
    const seconds = parseInt(parts.find(p => p.type === 'second')?.value || '0', 10);
    return { hours, minutes, seconds };
  };

  // Sort events for this day
  const dayEvents = [...events].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

  // Convert time to position percentage within the 8 AM - 12 AM range
  const getPosition = (hours, minutes) => {
    const totalMins = hours * 60 + minutes;
    const startMins = startHour * 60;
    const endMins = endHour * 60;
    
    // Clamp to range
    if (totalMins < startMins) return 0;
    if (totalMins > endMins) return 100;
    
    return ((totalMins - startMins) / totalMinutes) * 100;
  };

  // Format time from minutes to HH:MM AM/PM
  const formatTimeFromMinutes = (totalMinutes) => {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
    return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  // Calculate duration between two times in minutes
  const calculateDuration = (startMinute, endMinute) => {
    const durationMins = endMinute - startMinute;
    const hours = Math.floor(durationMins / 60);
    const mins = durationMins % 60;
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
  };

  // Build segments for visual bar - only show colored segments, grey is background
  // Logic:
  // - Before first check-in: Grey (empty background)
  // - From check-in to checkout: Present (green)
  // - After checkout: Grey (no segment) - do NOT show Away
  // - If employee returns same day: Show Away (red) ONLY between checkout and next check-in
  // - If checkout is missing between two checkins: Show Present (green) continuously
  // - If today and still checked in: Present bar continues to current time
  const buildSegments = () => {
    const segments = [];
    let lastCheckOutMinute = null;
    let lastCheckInMinute = null;

    dayEvents.forEach((event, index) => {
      const { hours, minutes } = getTimeInDisplayTimezone(event.created_at);
      const eventMinute = hours * 60 + minutes;
      
      // Only process events within our time range
      const clampedEventMinute = Math.max(startHour * 60, Math.min(endHour * 60, eventMinute));

      if (event.action === 'check_in') {
        // If there was a previous check-in without a checkout, add Present segment till this check-in
        if (lastCheckInMinute !== null && lastCheckOutMinute === null) {
          const clampedLastCheckIn = Math.max(startHour * 60, Math.min(endHour * 60, lastCheckInMinute));
          
          if (clampedEventMinute > clampedLastCheckIn) {
            const startPercent = getPosition(Math.floor(clampedLastCheckIn / 60), clampedLastCheckIn % 60);
            const endPercent = getPosition(Math.floor(clampedEventMinute / 60), clampedEventMinute % 60);
            const width = endPercent - startPercent;

            if (width > 0) {
              const lastCheckInTime = formatTimeFromMinutes(lastCheckInMinute);
              const currentCheckInTime = formatTimeFromMinutes(eventMinute);
              segments.push({
                id: `segment-present-continuous-${index}`,
                startPercent,
                width,
                type: 'present',
                startTime: lastCheckInTime,
                endTime: currentCheckInTime,
                startMinute: lastCheckInMinute,
                endMinute: eventMinute,
              });
            }
          }
        }
        // If there was a previous checkout, add an Away segment from checkout to this check-in
        else if (lastCheckOutMinute !== null) {
          const clampedLastCheckOut = Math.max(startHour * 60, Math.min(endHour * 60, lastCheckOutMinute));
          
          if (clampedEventMinute > clampedLastCheckOut) {
            const startPercent = getPosition(Math.floor(clampedLastCheckOut / 60), clampedLastCheckOut % 60);
            const endPercent = getPosition(Math.floor(clampedEventMinute / 60), clampedEventMinute % 60);
            const width = endPercent - startPercent;

            if (width > 0) {
              const lastCheckOutTime = formatTimeFromMinutes(lastCheckOutMinute);
              const currentCheckInTime = formatTimeFromMinutes(eventMinute);
              segments.push({
                id: `segment-away-${index}`,
                startPercent,
                width,
                type: 'away',
                startTime: lastCheckOutTime,
                endTime: currentCheckInTime,
                startMinute: lastCheckOutMinute,
                endMinute: eventMinute,
              });
            }
          }
        }
        // Record this check-in and reset checkout
        lastCheckInMinute = eventMinute;
        lastCheckOutMinute = null;
        
      } else if (event.action === 'check_out') {
        // If there's a previous checkout without an intervening check-in, mark away between them
        if (lastCheckOutMinute !== null && lastCheckInMinute === null) {
          const clampedPrevCheckout = Math.max(startHour * 60, Math.min(endHour * 60, lastCheckOutMinute));
          const clampedEventMinute = Math.max(startHour * 60, Math.min(endHour * 60, eventMinute));

          if (clampedEventMinute > clampedPrevCheckout) {
            const startPercent = getPosition(Math.floor(clampedPrevCheckout / 60), clampedPrevCheckout % 60);
            const endPercent = getPosition(Math.floor(clampedEventMinute / 60), clampedEventMinute % 60);
            const width = endPercent - startPercent;

            if (width > 0) {
              const prevCheckoutTime = formatTimeFromMinutes(lastCheckOutMinute);
              const currentCheckoutTime = formatTimeFromMinutes(eventMinute);
              segments.push({
                id: `segment-away-consecutive-${index}`,
                startPercent,
                width,
                type: 'away',
                startTime: prevCheckoutTime,
                endTime: currentCheckoutTime,
                startMinute: lastCheckOutMinute,
                endMinute: eventMinute,
              });
            }
          }
        }

        // Add Present segment from last check-in to this checkout
        if (lastCheckInMinute !== null) {
          const clampedLastCheckIn = Math.max(startHour * 60, Math.min(endHour * 60, lastCheckInMinute));
          
          if (clampedEventMinute > clampedLastCheckIn) {
            const startPercent = getPosition(Math.floor(clampedLastCheckIn / 60), clampedLastCheckIn % 60);
            const endPercent = getPosition(Math.floor(clampedEventMinute / 60), clampedEventMinute % 60);
            const width = endPercent - startPercent;

            if (width > 0) {
              const lastCheckInTime = formatTimeFromMinutes(lastCheckInMinute);
              const currentCheckOutTime = formatTimeFromMinutes(eventMinute);
              segments.push({
                id: `segment-present-${index}`,
                startPercent,
                width,
                type: 'present',
                startTime: lastCheckInTime,
                endTime: currentCheckOutTime,
                startMinute: lastCheckInMinute,
                endMinute: eventMinute,
              });
            }
          }
        }
        // Record this checkout and reset check-in
        lastCheckOutMinute = eventMinute;
        lastCheckInMinute = null;
      }
    });

    // Handle ongoing session (checked in but not checked out yet)
    // For today: show present till current time
    // For past days: show present till end of timeline
    if (lastCheckInMinute !== null) {
      const clampedLastCheckIn = Math.max(startHour * 60, Math.min(endHour * 60, lastCheckInMinute));
      
      if (isToday) {
        // Get current time and show present bar till now
        const now = new Date();
        const { hours: nowHours, minutes: nowMinutes } = getTimeInDisplayTimezone(now);
        const nowMinute = nowHours * 60 + nowMinutes;
        const clampedNow = Math.max(startHour * 60, Math.min(endHour * 60, nowMinute));
        
        if (clampedNow > clampedLastCheckIn) {
          const startPercent = getPosition(Math.floor(clampedLastCheckIn / 60), clampedLastCheckIn % 60);
          const endPercent = getPosition(Math.floor(clampedNow / 60), clampedNow % 60);
          const width = endPercent - startPercent;

          if (width > 0) {
            const lastCheckInTime = formatTimeFromMinutes(lastCheckInMinute);
            const currentTime = formatTimeFromMinutes(nowMinute);
            segments.push({
              id: 'segment-present-ongoing',
              startPercent,
              width,
              type: 'present',
              startTime: lastCheckInTime,
              endTime: currentTime,
              startMinute: lastCheckInMinute,
              endMinute: nowMinute,
            });
          }
        }
      } else {
        // For past days with unclosed session, show present till end of day timeline
        const endMinute = endHour * 60;
        if (endMinute > clampedLastCheckIn) {
          const startPercent = getPosition(Math.floor(clampedLastCheckIn / 60), clampedLastCheckIn % 60);
          const width = 100 - startPercent;

          if (width > 0) {
            const lastCheckInTime = formatTimeFromMinutes(lastCheckInMinute);
            const endTime = formatTimeFromMinutes(endMinute);
            segments.push({
              id: 'segment-present-unclosed',
              startPercent,
              width,
              type: 'present',
              startTime: lastCheckInTime,
              endTime: endTime,
              startMinute: lastCheckInMinute,
              endMinute: endMinute,
            });
          }
        }
      }
    }

    return segments;
  };

  const segments = buildSegments();

  // Event markers with click handling
  const eventMarkers = dayEvents.map((event, idx) => {
    const isMissingEntry = !!event.is_missing_entry;
    const { hours, minutes, seconds } = getTimeInDisplayTimezone(event.created_at);
    const markerMinute = (hours * 60) + minutes + ((seconds || 0) / 60);
    const position = getPosition(Math.floor(markerMinute / 60), markerMinute % 60);
    const markerId = `${event.attendance_id ?? 'evt'}-${event.action}-${event.created_at}-${idx}`;
    const timelineEventKey = [
      event.snapshot_audit_id ? `audit:${event.snapshot_audit_id}` : 'audit:none',
      event.attendance_id ? `attendance:${event.attendance_id}` : 'attendance:none',
      `action:${event.action || 'evt'}`,
      `at:${event.created_at || ''}`,
    ].join('|');
    const missingLabel = event.action === 'check_in' ? 'Missing In' : 'Missing Out';
    return {
      id: markerId,
      attendanceId: event.attendance_id || null,
      timelineEventKey,
      position,
      minute: markerMinute,
      isCheckIn: event.action === 'check_in',
      isMissingEntry,
      missingLabel,
      time: isMissingEntry ? '--:--:--' : formatDate(event.created_at, true, false),
      rawTime: new Date(event.created_at),
      event, // Include full event for image resolution
    };
  });

  const spreadEventMarkers = useMemo(
    () => eventMarkers.map((marker) => ({ ...marker, offsetXPx: 0, offsetYPx: 0 })),
    [eventMarkers]
  );

  // Hour labels - show every hour for better precision
  const hourLabels = [];
  for (let h = startHour; h <= endHour; h += 1) {
    const label = h === 12 ? '12 PM' : h === 24 ? '12 AM' : h > 12 ? `${h - 12} PM` : `${h} AM`;
    const position = getPosition(h, 0);
    if (h === startHour || h === endHour || h % 2 === 0) {
      hourLabels.push({ hour: h, label, position });
    }
  }

  const startM = startHour * 60;
  const endM = endHour * 60;
  const effectiveStart = zoomState ? zoomState.visibleStartMinute : startM;
  const effectiveEnd = zoomState ? zoomState.visibleEndMinute : endM;
  const effectiveSpan = effectiveEnd - effectiveStart;
  const getPositionDisplay = useCallback(
    (minute) => Math.max(0, Math.min(100, ((minute - effectiveStart) / effectiveSpan) * 100)),
    [effectiveStart, effectiveSpan]
  );

  const displaySegments = useMemo(() => {
    if (!zoomState) return segments;
    return segments
      .filter((s) => s.startMinute < effectiveEnd && s.endMinute > effectiveStart)
      .map((s) => {
        const clipStart = Math.max(s.startMinute, effectiveStart);
        const clipEnd = Math.min(s.endMinute, effectiveEnd);
        const startP = ((clipStart - effectiveStart) / effectiveSpan) * 100;
        const endP = ((clipEnd - effectiveStart) / effectiveSpan) * 100;
        return { ...s, startPercent: startP, width: endP - startP };
      });
  }, [zoomState, segments, effectiveStart, effectiveEnd, effectiveSpan]);

  const displayMarkers = useMemo(() => {
    const positioned = (!zoomState
      ? spreadEventMarkers.map((m, i) => ({ ...m, eventIndex: i }))
      : spreadEventMarkers
          .map((m, i) => ({ ...m, eventIndex: i }))
          .filter((item) => item.minute >= effectiveStart && item.minute <= effectiveEnd)
          .map((item) => ({
            ...item,
            position: ((item.minute - effectiveStart) / effectiveSpan) * 100,
          })));

    // Collision-aware horizontal spreading for close markers.
    const sorted = [...positioned].sort((a, b) => {
      const posDiff = a.position - b.position;
      if (Math.abs(posDiff) > 1e-4) return posDiff;
      const timeDiff = (a.rawTime?.getTime?.() || 0) - (b.rawTime?.getTime?.() || 0);
      if (timeDiff !== 0) return timeDiff;
      if (a.isMissingEntry !== b.isMissingEntry) return a.isMissingEntry ? -1 : 1;
      return String(a.id).localeCompare(String(b.id));
    });
    const barWidthPx = barRef.current?.clientWidth || 0;
    const markerDiameterPx = 14;
    const minGapPercent = barWidthPx > 0 ? ((markerDiameterPx / barWidthPx) * 100) : (zoomState ? 1.0 : 1.2);

    const groups = [];
    sorted.forEach((marker) => {
      const lastGroup = groups[groups.length - 1];
      if (!lastGroup) {
        groups.push([marker]);
        return;
      }
      const lastMarkerInGroup = lastGroup[lastGroup.length - 1];
      if ((marker.position - lastMarkerInGroup.position) <= minGapPercent) {
        lastGroup.push(marker);
      } else {
        groups.push([marker]);
      }
    });

    const spreadStepPx = 10;
    const xOffsetById = new Map();
    groups.forEach((group) => {
      if (group.length <= 1) {
        xOffsetById.set(group[0].id, 0);
        return;
      }

      const center = (group.length - 1) / 2;
      group.forEach((marker, index) => {
        xOffsetById.set(marker.id, (index - center) * spreadStepPx);
      });
    });

    return positioned.map((marker) => ({
      ...marker,
      offsetXPx: xOffsetById.get(marker.id) || 0,
      offsetYPx: 0,
    }));
  }, [zoomState, spreadEventMarkers, effectiveStart, effectiveEnd, effectiveSpan]);

  // When zoomed: show finer time labels (minutes) based on visible span
  const displayHourLabels = useMemo(() => {
    if (!zoomState) return hourLabels;
    const spanMins = effectiveSpan;
    // Choose label step (minutes) and whether to show minutes based on zoom level
    let stepMins = 60;
    let showMinutes = false;
    if (spanMins <= 30) {
      stepMins = 5;
      showMinutes = true;
    } else if (spanMins <= 60) {
      stepMins = 10;
      showMinutes = true;
    } else if (spanMins <= 120) {
      stepMins = 15;
      showMinutes = true;
    } else if (spanMins <= 240) {
      stepMins = 30;
      showMinutes = true;
    } else {
      stepMins = 60;
      showMinutes = false;
    }
    const labels = [];
    const startM = Math.floor(effectiveStart / stepMins) * stepMins;
    for (let m = startM; m <= effectiveEnd; m += stepMins) {
      if (m < effectiveStart) continue;
      const position = ((m - effectiveStart) / effectiveSpan) * 100;
      const label = showMinutes
        ? formatTimeFromMinutes(m)
        : (() => {
            const h = Math.floor(m / 60);
            return h === 12 ? '12 PM' : h === 24 ? '12 AM' : h > 12 ? `${h - 12} PM` : `${h} AM`;
          })();
      labels.push({ hour: Math.floor(m / 60), label, position });
    }
    return labels;
  }, [zoomState, hourLabels, effectiveStart, effectiveEnd, effectiveSpan]);

  // Current time marker (when zoomed, only if now in range)
  const now = new Date();
  const { hours: nowHours, minutes: nowMinutes } = getTimeInDisplayTimezone(currentTime);
  const nowMinute = nowHours * 60 + nowMinutes;
  const nowPosition = zoomState
    ? (nowMinute >= effectiveStart && nowMinute <= effectiveEnd ? ((nowMinute - effectiveStart) / effectiveSpan) * 100 : null)
    : getPosition(nowHours, nowMinutes);
  const showNowMarker = isToday && (zoomState ? nowPosition !== null : nowHours >= startHour && nowHours < endHour);

  // Clamp visible range to [startM, endM] and enforce min/max span
  const clampZoomRange = useCallback((visibleStart, visibleEnd) => {
    let span = visibleEnd - visibleStart;
    span = Math.max(ZOOM_MIN_SPAN_MINS, Math.min(ZOOM_MAX_SPAN_MINS, span));
    let start = visibleStart;
    let end = visibleEnd;
    if (end - start !== span) {
      const center = (start + end) / 2;
      start = center - span / 2;
      end = center + span / 2;
    }
    if (start < startM) {
      start = startM;
      end = Math.min(endM, start + span);
    }
    if (end > endM) {
      end = endM;
      start = Math.max(startM, end - span);
    }
    return { visibleStartMinute: start, visibleEndMinute: end };
  }, []);

  // Calculate presence time for this day
  const calculateDayPresence = () => {
    const startRange = startHour * 60;
    const endRange = endHour * 60;
    let totalMins = 0;
    let lastCheckInMinute = null;

    dayEvents.forEach((event) => {
      const { hours, minutes } = getTimeInDisplayTimezone(event.created_at);
      const eventMinute = hours * 60 + minutes;
      const clampedEventMinute = Math.max(startRange, Math.min(endRange, eventMinute));

      if (event.action === 'check_in') {
        // If there was a previous check-in without checkout, add present till this check-in
        if (lastCheckInMinute !== null) {
          const clampedStart = Math.max(startRange, Math.min(endRange, lastCheckInMinute));
          if (clampedEventMinute > clampedStart) {
            totalMins += clampedEventMinute - clampedStart;
          }
        }
        lastCheckInMinute = eventMinute;
      } else if (event.action === 'check_out') {
        if (lastCheckInMinute !== null) {
          const clampedStart = Math.max(startRange, Math.min(endRange, lastCheckInMinute));
          if (clampedEventMinute > clampedStart) {
            totalMins += clampedEventMinute - clampedStart;
          }
        }
        lastCheckInMinute = null;
      }
    });

    // If still checked in (no checkout after last check-in)
    if (lastCheckInMinute !== null) {
      const clampedStart = Math.max(startRange, Math.min(endRange, lastCheckInMinute));
      let endMinute = endRange;
      if (isToday) {
        const nowTz = getTimeInDisplayTimezone(currentTime);
        endMinute = Math.max(startRange, Math.min(endRange, nowTz.hours * 60 + nowTz.minutes));
      }
      if (endMinute > clampedStart) {
        totalMins += endMinute - clampedStart;
      }
    }

    const hours = Math.floor(totalMins / 60);
    const mins = totalMins % 60;
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
  };

  const handleMarkerClick = (e, marker) => {
    e.stopPropagation();
    setClickedMarker(clickedMarker?.id === marker.id ? null : marker);
  };

  // Show a hover guide with time + segment details (present/away) under cursor; drag hint when over bar or container
  const handleHoverMove = (event) => {
    const overBar = barRef.current && barRef.current.contains(event?.target);
    if (!overBar) {
      // Over container but not bar (e.g. over drag-hint text) – show hint only
      setHoverInfo(null);
      setDragHintHover(true);
      return;
    }
    // Skip hover guide when pointer is over a marker so only the marker tooltip shows
    if (event?.target) {
      const target = event.target;
      if (target.dataset?.marker === 'true' || (target.closest && target.closest('[data-marker="true"]'))) {
        setHoverInfo(null);
        setDragHintHover(true);
        return;
      }
    }

    if (!barRef.current) return;
    const rect = barRef.current.getBoundingClientRect();
    const relativeX = Math.max(0, Math.min(rect.width, event.clientX - rect.left));
    const percent = (relativeX / rect.width) * 100;
    const hoverMinute = zoomState
      ? effectiveStart + (percent / 100) * effectiveSpan
      : startHour * 60 + (percent / 100) * totalMinutes;
    const minuteFromStart = Math.max(startM, Math.min(endM, Math.round(hoverMinute)));
    const activeSegment = displaySegments.find(
      (segment) => percent >= segment.startPercent && percent <= segment.startPercent + segment.width
    );

    if (activeSegment) {
      setHoverInfo({
        percent,
        timeLabel: formatTimeFromMinutes(minuteFromStart),
        segment: activeSegment,
        duration: calculateDuration(activeSegment.startMinute, activeSegment.endMinute),
      });
      setDragHintHover(false);
    } else {
      setHoverInfo(null);
      setDragHintHover(true);
    }
  };

  const handleHoverLeave = () => {
    setHoverInfo(null);
    setDragHintHover(false);
  };

  // --- Binance-style zoom & pan ---

  // Wheel zoom: anchor at mouse position (smooth, throttled with rAF)
  const handleWheel = useCallback(
    (e) => {
      const rect = barRef.current?.getBoundingClientRect();
      if (!rect) return;
      const delta = e.deltaY;
      if (delta === 0) return;
      const now = Date.now();
      if (now - lastWheelRef.current < 20) return;
      lastWheelRef.current = now;

      setZoomState((prev) => {
        const curStart = prev ? prev.visibleStartMinute : startM;
        const curEnd = prev ? prev.visibleEndMinute : endM;
        const curSpan = curEnd - curStart;
        const relX = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
        const pointerMinute = curStart + relX * curSpan;
        const factor = delta > 0 ? ZOOM_WHEEL_FACTOR : 1 / ZOOM_WHEEL_FACTOR;
        let newSpan = curSpan * factor;
        newSpan = Math.max(ZOOM_MIN_SPAN_MINS, Math.min(ZOOM_MAX_SPAN_MINS, newSpan));
        const f = (pointerMinute - curStart) / curSpan;
        let newStart = pointerMinute - f * newSpan;
        let newEnd = newStart + newSpan;
        const clamped = clampZoomRange(newStart, newEnd);
        return clamped;
      });
    },
    [clampZoomRange]
  );

  // Pan: start drag (mouse or single touch when zoomed)
  const handlePanStart = useCallback(
    (clientX) => {
      if (!zoomState) return;
      setIsPanning(true);
      panRef.current = {
        active: true,
        startX: clientX,
        startStart: zoomState.visibleStartMinute,
        startEnd: zoomState.visibleEndMinute,
      };
    },
    [zoomState]
  );

  const handlePanMove = useCallback((clientX) => {
    if (!panRef.current.active) return;
    const rect = barRef.current?.getBoundingClientRect();
    if (!rect) return;
    const deltaX = clientX - panRef.current.startX;
    const span = panRef.current.startEnd - panRef.current.startStart;
    const deltaMinute = (deltaX / rect.width) * span;
    let newStart = panRef.current.startStart - deltaMinute;
    let newEnd = panRef.current.startEnd - deltaMinute;
    const clamped = clampZoomRange(newStart, newEnd);
    setZoomState(clamped);
  }, []);

  const handlePanEnd = useCallback(() => {
    panRef.current.active = false;
    setIsPanning(false);
  }, []);

  // Mouse: pan start/move/end
  const handleMouseDown = useCallback(
    (e) => {
      if (e.target?.dataset?.marker === 'true' || e.target?.closest?.('[data-marker="true"]')) return;
      if (zoomState) {
        e.preventDefault();
        handlePanStart(e.clientX);
      }
    },
    [zoomState, handlePanStart]
  );

  useEffect(() => {
    const onMouseMove = (e) => handlePanMove(e.clientX);
    const onMouseUp = () => handlePanEnd();
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, [handlePanMove, handlePanEnd]);

  // Double-click: reset zoom
  const handleDoubleClick = useCallback(() => {
    setZoomState(null);
  }, []);

  const handleZoomStep = useCallback((direction) => {
    setZoomState((prev) => {
      const curStart = prev ? prev.visibleStartMinute : startM;
      const curEnd = prev ? prev.visibleEndMinute : endM;
      const curSpan = curEnd - curStart;
      const zoomFactor = direction === 'in' ? 1 / 1.2 : 1.2;
      let nextSpan = curSpan * zoomFactor;
      nextSpan = Math.max(ZOOM_MIN_SPAN_MINS, Math.min(ZOOM_MAX_SPAN_MINS, nextSpan));
      if (nextSpan >= ZOOM_MAX_SPAN_MINS - 1) {
        return null;
      }
      const center = (curStart + curEnd) / 2;
      const nextStart = center - nextSpan / 2;
      const nextEnd = center + nextSpan / 2;
      return clampZoomRange(nextStart, nextEnd);
    });
  }, [clampZoomRange]);

  const currentZoomSpan = zoomState
    ? zoomState.visibleEndMinute - zoomState.visibleStartMinute
    : ZOOM_MAX_SPAN_MINS;
  const disableZoomIn = currentZoomSpan <= ZOOM_MIN_SPAN_MINS + 1;
  const disableZoomOut = currentZoomSpan >= ZOOM_MAX_SPAN_MINS - 1;
  const zoomPercent = `${Math.round((ZOOM_MAX_SPAN_MINS / currentZoomSpan) * 100)}%`;

  // Touch: pinch zoom (2 fingers) and pan (1 finger when zoomed)
  useEffect(() => {
    const el = barContainerRef.current;
    if (!el) return;
    const getDist = (touches) =>
      touches?.length >= 2 ? Math.hypot(touches[1].clientX - touches[0].clientX, touches[1].clientY - touches[0].clientY) : 0;
    const getCenterX = (touches) => (touches?.length >= 2 ? (touches[0].clientX + touches[1].clientX) / 2 : 0);

    const onTouchStart = (e) => {
      const currentZoom = zoomStateRef.current;
      if (e.touches.length === 2) {
        e.preventDefault();
        const dist = getDist(e.touches);
        if (dist >= 5) {
          const rect = el.getBoundingClientRect();
          const centerX = getCenterX(e.touches);
          const relX = Math.max(0, Math.min(1, (centerX - rect.left) / rect.width));
          const curStart = currentZoom ? currentZoom.visibleStartMinute : startM;
          const curEnd = currentZoom ? currentZoom.visibleEndMinute : endM;
          const span = curEnd - curStart;
          const centerMinute = curStart + relX * span;
          pinchRef.current = { initialDistance: dist, initialSpan: span, initialCenter: centerMinute };
        }
      } else if (e.touches.length === 1 && currentZoom) {
        handlePanStart(e.touches[0].clientX);
      }
    };

    const onTouchMove = (e) => {
      if (e.touches.length === 2 && pinchRef.current.initialDistance > 0) {
        e.preventDefault();
        const dist = getDist(e.touches);
        if (dist < 5) return;
        const scale = dist / pinchRef.current.initialDistance;
        let newSpan = pinchRef.current.initialSpan / scale;
        newSpan = Math.max(ZOOM_MIN_SPAN_MINS, Math.min(ZOOM_MAX_SPAN_MINS, newSpan));
        const center = pinchRef.current.initialCenter;
        let newStart = center - newSpan / 2;
        let newEnd = center + newSpan / 2;
        setZoomState(clampZoomRange(newStart, newEnd));
        pinchRef.current = { ...pinchRef.current, initialDistance: dist };
      } else if (e.touches.length === 1 && panRef.current.active) {
        handlePanMove(e.touches[0].clientX);
      }
    };

    const onTouchEnd = (e) => {
      if (e.touches.length < 2) pinchRef.current = { initialDistance: 0, initialSpan: 0, initialCenter: 0 };
      if (e.touches.length < 1) handlePanEnd();
    };

    el.addEventListener('touchstart', onTouchStart, { passive: false });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd, { passive: true });
    el.addEventListener('touchcancel', onTouchEnd, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [handlePanStart, handlePanMove, handlePanEnd, clampZoomRange]);

  // Wheel listener (non-passive so we can preventDefault when needed)
  useEffect(() => {
    const el = barContainerRef.current;
    if (!el) return;
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);

  return (
    <Box sx={{ mb: 3 }}>
      {/* Date Header Row */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        spacing={{ xs: 0.5, sm: 0 }}
        sx={{ mb: 1.5 }}
      >
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              color: '#1e293b',
              minWidth: { xs: 'auto', sm: 140 },
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
            }}
          >
            {date}
          </Typography>
          {isToday && (
            <Chip size="small" label="Today" sx={{ height: { xs: 18, sm: 20 }, fontSize: { xs: '0.6rem', sm: '0.65rem' }, bgcolor: '#dbeafe', color: '#1d4ed8' }} />
          )}
        </Stack>
        {!hideSummary && (
          <Stack direction="row" spacing={0.5} alignItems="center">
            {showInOutCounts && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                {dayEvents.filter(e => e.action === 'check_in').length} in / {dayEvents.filter(e => e.action === 'check_out').length} out
              </Typography>
            )}
            <Chip size="small" label={summaryDurationLabel ?? calculateDayPresence()} sx={{ height: { xs: 16, sm: 18 }, fontSize: { xs: '0.55rem', sm: '0.6rem' }, bgcolor: '#dcfce7', color: '#166534' }} />
          </Stack>
        )}
      </Stack>

      {/* Fixed-height row for zoom controls */}
      <Stack direction="row" justifyContent="flex-end" alignItems="center" sx={{ minHeight: 32, mb: 0.5 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.25,
            px: 0.4,
            py: 0.2,
            borderRadius: 2,
            border: '1px solid #cbd5e1',
            bgcolor: '#f8fafc',
          }}
        >
          <Tooltip title="Zoom out" arrow>
            <span>
              <IconButton
                size="small"
                onClick={() => handleZoomStep('out')}
                disabled={disableZoomOut}
                sx={{ width: 26, height: 26, borderRadius: 1.5 }}
              >
                <RemoveIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Chip
            size="small"
            label={zoomPercent}
            variant="filled"
            sx={{
              height: 24,
              minWidth: 58,
              fontSize: '0.7rem',
              fontWeight: 700,
              bgcolor: '#e2e8f0',
              color: '#0f172a',
            }}
          />
          <Tooltip title="Zoom in" arrow>
            <span>
              <IconButton
                size="small"
                onClick={() => handleZoomStep('in')}
                disabled={disableZoomIn}
                sx={{ width: 26, height: 26, borderRadius: 1.5 }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Reset zoom" arrow>
            <span>
              <IconButton
                size="small"
                onClick={() => setZoomState(null)}
                disabled={!zoomState}
                sx={{ width: 26, height: 26, borderRadius: 1.5 }}
              >
                <RestartAltIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Stack>

      {/* Timeline Bar – Binance-style: wheel zoom (anchor at pointer), 2-finger pinch, drag to pan, double-click reset */}
      <Box
        ref={barContainerRef}
        onMouseDown={handleMouseDown}
        onDoubleClick={handleDoubleClick}
        onMouseMove={handleHoverMove}
        onMouseLeave={handleHoverLeave}
        sx={{ position: 'relative', mb: 0.5, touchAction: 'none', userSelect: 'none', cursor: zoomState ? 'grab' : undefined }}
      >
        <Box
          ref={barRef}
          sx={{
            position: 'relative',
            height: 36,
            transition: `opacity ${ZOOM_TRANSITION_MS}ms ease-out`,
            ...(isPanning && { cursor: 'grabbing' }),
          }}
        >
        {/* Grey Background Track */}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: 0,
            right: 0,
            height: 14,
            transform: 'translateY(-50%)',
            bgcolor: '#e5e7eb',
            borderRadius: 7,
            overflow: 'hidden',
          }}
        >
          {/* Colored Segments – use displaySegments when zoomed */}
          {displaySegments.map((segment) => (
            <Box
              key={segment.id}
              sx={{
                position: 'absolute',
                top: 0,
                left: `${segment.startPercent}%`,
                width: `${segment.width}%`,
                height: '100%',
                bgcolor: segment.type === 'present' ? '#86efac' : '#fecaca', // Brighter colors
                borderRadius: segment.startPercent === 0 ? '7px 0 0 7px' : segment.startPercent + segment.width >= 99.9 ? '0 7px 7px 0' : 0,
                cursor: 'pointer',
                transition: 'opacity 0.2s',
                '&:hover': {
                  opacity: 0.8,
                },
              }}
            />
          ))}
        </Box>

        {/* Event Markers – use displayMarkers when zoomed (optional for workstation). */}
        {!hideMarkers && displayMarkers.map((marker) => {
          const isExternallyHighlighted = !!highlightEventKey && marker.timelineEventKey === highlightEventKey;
          return (
            <Box
              key={marker.id}
              data-marker="true"
              sx={{
                position: 'absolute',
                top: 0,
                left: `calc(${marker.position}% + ${marker.offsetXPx || 0}px)`,
                transform: `translate(-50%, ${marker.offsetYPx || 0}px)`,
                zIndex: 2,
                height: '100%',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {/* Time Badge - shown on click */}
              {clickedMarker?.id === marker.id && (
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: '100%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    mb: 0.5,
                    px: 1,
                    py: 0.5,
                    bgcolor: marker.isMissingEntry ? '#334155' : (marker.isCheckIn ? '#166534' : '#92400e'),
                    color: '#fff',
                    borderRadius: 1,
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: '100%',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      border: '5px solid transparent',
                      borderTopColor: marker.isMissingEntry ? '#334155' : (marker.isCheckIn ? '#166534' : '#92400e'),
                    },
                  }}
                >
                  {marker.isMissingEntry ? marker.missingLabel : (marker.isCheckIn ? 'IN' : 'OUT')}<br />
                  {marker.time}
                </Box>
              )}

              {/* Marker Line */}
              <Tooltip
                title={!clickedMarker || clickedMarker.id !== marker.id ? (
                  <Box sx={{ textAlign: 'center', p: 0.5, minWidth: 80 }}>
                    {getAuditImageKey(marker.event) ? (
                      <AuditImg
                        auditId={getAuditImageKey(marker.event)}
                        alt={marker.isCheckIn ? 'Check-in' : 'Check-out'}
                        sx={{
                          width: 70,
                          height: 70,
                          borderRadius: 1,
                          mb: 0.75,
                          border: '2px solid',
                          borderColor: marker.isCheckIn ? '#4ade80' : '#fbbf24',
                          overflow: 'hidden',
                          mx: 'auto',
                        }}
                        imgSx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <Box
                        component="img"
                        src={resolveAttendanceLogDisplayUrl(marker.event, resolveCachedImage)}
                        alt={marker.isCheckIn ? 'Check-in' : 'Check-out'}
                        sx={{
                          width: 70,
                          height: 70,
                          borderRadius: 1,
                          objectFit: 'cover',
                          mb: 0.75,
                          border: '2px solid',
                          borderColor: marker.isCheckIn ? '#4ade80' : '#fbbf24',
                        }}
                      />
                    )}
                    <Typography variant="caption" sx={{ fontWeight: 700, color: marker.isCheckIn ? '#4ade80' : '#fbbf24', display: 'block' }}>
                      {marker.isMissingEntry ? marker.missingLabel : (marker.isCheckIn ? 'Check In' : 'Check Out')}
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, color: '#fff' }}>{marker.time}</Typography>
                  </Box>
                ) : ''}
                arrow
                placement="top"
                onOpen={() => {
                  if (typeof onRequestMarkerImage !== 'function') return;
                  if (!attendanceLogHasImagePath(marker.event)) return;
                  onRequestMarkerImage(marker.event);
                }}
                componentsProps={{
                  tooltip: {
                    sx: {
                      bgcolor: 'rgba(30, 41, 59, 0.95)',
                      backdropFilter: 'blur(8px)',
                      borderRadius: 1.5,
                      boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                    }
                  }
                }}
              >
                <Box
                  onClick={(e) => handleMarkerClick(e, marker)}
                    data-marker="true"
                  sx={{
                    width: 14,
                    height: 14,
                    borderRadius: '50%',
                    bgcolor: marker.isMissingEntry ? '#64748b' : (marker.isCheckIn ? '#22c55e' : '#f59e0b'),
                    border: '2px solid #fff',
                    boxShadow: isExternallyHighlighted
                    ? '0 0 0 3px rgba(59, 130, 246, 0.28), 0 3px 10px rgba(37, 99, 235, 0.45)'
                    : '0 2px 4px rgba(0,0,0,0.2)',
                    cursor: 'pointer',
                    transition: 'transform 0.15s, box-shadow 0.15s',
                    transform: isExternallyHighlighted ? 'scale(1.35)' : 'scale(1)',
                    '&:hover': {
                      transform: 'scale(1.2)',
                      boxShadow: '0 3px 8px rgba(0,0,0,0.3)',
                    },
                  }}
                />
              </Tooltip>
            </Box>
          )
        })}

        {/* Hover guide line + tooltip */}
        {hoverInfo && hoverInfo.segment && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: `${hoverInfo.percent}%`,
              transform: 'translateX(-50%)',
              height: '100%',
              zIndex: 5,
              pointerEvents: 'none',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: (hoverInfo.fixedPos?.top || 0) - 30,
                bottom: 10,
                left: hoverInfo.fixedPos?.left || 0,
                // height: 30,
                borderLeft: '1px dashed rgb(198, 205, 214)',
                zIndex: 1499,
                pointerEvents: 'none',
              }}
            />

            {/* Tooltip - Fixed on screen */}
            <Box
              sx={{
                position: 'absolute',
                top: (hoverInfo.fixedPos?.top || 0) - 30,
                left: hoverInfo.fixedPos?.left || 0,
                
                transform: hoverInfo.percent < 20
                  ? 'translate(0, -100%)'
                  : hoverInfo.percent > 95
                    ? 'translate(-100%, -100%)'
                    : 'translate(-50%, -100%)',
                zIndex: 1500,
                pointerEvents: 'none',
                bgcolor: '#0f172a',
                color: '#fff',
                px: 1,
                py: 0.75,
                borderRadius: 1.5,
                boxShadow: '0 4px 14px rgba(0,0,0,0.25)',
                minWidth: 140,
                textAlign: 'center',
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', color: hoverInfo.segment.type === 'present' ? '#86efac' : '#fecaca' }}>
                {hoverInfo.segment.type === 'present' ? (hideMarkers ? 'Occupied' : 'Present') : 'Away'} · {hoverInfo.timeLabel}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', color: '#e2e8f0', fontWeight: 600 }}>
                {hoverInfo.segment.startTime} - {hoverInfo.segment.endTime}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', color: '#cbd5e1', fontWeight: 500 }}>
                Duration: {hoverInfo.duration}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Current Time Marker - Location Pin Style */}
        {showNowMarker && (
          <Tooltip title={`Now: ${format(now, 'hh:mm a')}`} arrow placement="top">
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: `${nowPosition}%`,
                transform: 'translate(-50%, -50%)',
                zIndex: 4,
                cursor: 'pointer',
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  width: 20,
                  height: 28,
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                }}
              >
                {/* Pin Head */}
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: '50% 50% 50% 0',
                    transform: 'rotate(-45deg)',
                    bgcolor: '#374151',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '&::after': {
                      content: '""',
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      bgcolor: '#fff',
                      transform: 'rotate(45deg)',
                    },
                  }}
                />
              </Box>
            </Box>
          </Tooltip>
        )}
        </Box>
      </Box>

      {/* Hour Labels – use displayHourLabels when zoomed */}
      <Box sx={{ position: 'relative', height: { xs: 16, sm: 18 } }}>
        {displayHourLabels.map((item, idx) => {
          // On mobile, only show every 4th label for cleaner look
          const isMobileLabel = typeof window !== 'undefined' && window.innerWidth < 600;
          const skipOnMobile = isMobileLabel && idx % 2 !== 0 && item.hour !== startHour && item.hour !== endHour;
          if (skipOnMobile) return null;

          return (
            <Typography
              key={zoomState ? `${item.hour}-${item.position}` : item.hour}
              variant="caption"
              sx={{
                position: 'absolute',
                left: `${item.position}%`,
                transform: 'translateX(-50%)',
                color: '#6b7280',
                fontSize: { xs: '0.5rem', sm: '0.6rem' },
                fontWeight: 500,
                whiteSpace: 'nowrap',
              }}
            >
              {item.label}
            </Typography>
          );
        })}
      </Box>

      {/* Show drag hint label whenever zoomed (same time as Reset zoom button) */}
      <Box sx={{ minHeight: 20, mt: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {zoomState && (
          <Stack direction="row" alignItems="center" spacing={1}>
            {/* Left Arrow Button */}
            <Grow in={zoomState} timeout={300}>
              <IconButton
                size="small"
                onClick={() => {
                  if (!zoomState) return;
                  const span = effectiveEnd - effectiveStart;
                  const panAmount = span * 0.2; // Pan 20% of visible span
                  const newStart = Math.max(startM, effectiveStart - panAmount);
                  const newEnd = newStart + span;
                  setZoomState(clampZoomRange(newStart, newEnd));
                }}
                disabled={!zoomState}
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.9)',
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 1)',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                  },
                  '&:disabled': {
                    opacity: 0.5,
                    cursor: 'not-allowed',
                  }
                }}
              >
                <ChevronLeft fontSize="small" />
              </IconButton>
            </Grow>

            <Grow in={zoomState} timeout={300}>
              <Typography
                variant="caption"
                sx={{
                  textAlign: 'center',
                  color: '#94a3b8',
                  fontSize: '0.65rem',
                  pointerEvents: 'none',
                }}
              >
                Drag left or right to adjust timeline
              </Typography>
            </Grow>

            {/* Right Arrow Button */}
            <Grow in={zoomState} timeout={300}>
              <IconButton
                size="small"
                onClick={() => {
                  if (!zoomState) return;
                  const span = effectiveEnd - effectiveStart;
                  const panAmount = span * 0.2; // Pan 20% of visible span
                  const newEnd = Math.min(endM, effectiveEnd + panAmount);
                  const newStart = newEnd - span;
                  setZoomState(clampZoomRange(newStart, newEnd));
                }}
                disabled={!zoomState}
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.9)',
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 1)',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                  },
                  '&:disabled': {
                    opacity: 0.5,
                    cursor: 'not-allowed',
                  }
                }}
              >
                <ChevronRight fontSize="small" />
              </IconButton>
            </Grow>
          </Stack>
        )}
      </Box>
    </Box>
  );
};

// Timeline Modal Component - Visual timeline bars grouped by date
export const TimelineModal = ({ open, onClose, summary, loading = false, thumbnail, snapshotCache, lateEntryTime = null, lateEntryTrackingEnabled = true, onRequestTimelineEventImage = null }) => {
  const theme = useTheme();
  const isMobileModal = useMediaQuery(theme.breakpoints.down('sm'));
  const [currentTime, setCurrentTime] = useState(new Date());
  const { openImagePreview } = useImagePreview();

  // Update current time every 30 seconds to keep presence time live
  useEffect(() => {
    if (!open) return;
    
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 30000); // Update every 30 seconds
    
    return () => clearInterval(interval);
  }, [open]);

  if (!summary) return null;

  const {
    person_name,
    person_id,
    total_check_ins,
    total_check_outs,
    recent_events = [],
    employee_code,
    employee_type,
  } = summary;

  const avatarLetter = (person_name || person_id || '?').charAt(0).toUpperCase();
  const handleProfileImagePreview = (event) => {
    if (!thumbnail) return;
    event.preventDefault();
    event.stopPropagation();
    openImagePreview({
      src: thumbnail,
      title: person_name || 'Profile image',
      alt: person_name || 'Profile image',
      employeeName: person_name || 'Unknown person',
      employeeId: employee_code || person_id || '',
      employeeImage: thumbnail,
      hideTitle: true,
    });
  };

  // Sort events chronologically (most recent first for display)
  const sortedEvents = [...recent_events].sort((a, b) =>
    new Date(b.created_at) - new Date(a.created_at)
  );

  // Group events by date
  const groupEventsByDate = () => {
    const groups = {};
    recent_events.forEach((event) => {
      const eventDate = new Date(event.created_at);
      const dateKey = format(eventDate, 'yyyy-MM-dd');
      const dateLabel = format(eventDate, 'EEE, MMM dd, yyyy');
      
      if (!groups[dateKey]) {
        groups[dateKey] = {
          dateKey,
          dateLabel,
          events: [],
        };
      }
      groups[dateKey].events.push(event);
    });

    // Sort dates (most recent first)
    return Object.values(groups).sort((a, b) => b.dateKey.localeCompare(a.dateKey));
  };

  const groupedDays = groupEventsByDate();

  const startHour = 8;
  const endHour = 24;

  // Helper to get hours and minutes in browser's timezone (same as DayTimelineBar)
  const getTimeInDisplayTimezone = (dateValue) => {
    let d = null;
    if (dateValue instanceof Date) {
      d = dateValue;
    } else if (typeof dateValue === 'string') {
      const trimmed = dateValue.trim();
      const utcString = trimmed.endsWith('Z') ? trimmed : trimmed + 'Z';
      d = new Date(utcString);
    } else {
      d = new Date(dateValue);
    }
    
    if (!d || isNaN(d.getTime())) return { hours: 0, minutes: 0 };
    
    // Get time parts in the browser's timezone (defaults to user's local timezone)
    const formatter = new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: 'numeric',
      hour12: false,
    });
    const parts = formatter.formatToParts(d);
    const hours = parseInt(parts.find(p => p.type === 'hour')?.value || '0', 10);
    const minutes = parseInt(parts.find(p => p.type === 'minute')?.value || '0', 10);
    return { hours, minutes };
  };

  // Calculate total presence time across all days using the same method as DayTimelineBar
  const calculateTotalPresence = () => {
    let totalMins = 0;
    
    // Calculate presence for each day separately and sum them up
    groupedDays.forEach((day) => {
      const dayEvents = [...day.events].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      const dayDateKey = day.dateKey;
      const isDayToday = isToday(dayDateKey);
      
      const startRange = startHour * 60;
      const endRange = endHour * 60;
      let lastCheckInMinute = null;

      dayEvents.forEach((event) => {
        const { hours, minutes } = getTimeInDisplayTimezone(event.created_at);
        const eventMinute = hours * 60 + minutes;
        const clampedEventMinute = Math.max(startRange, Math.min(endRange, eventMinute));

        if (event.action === 'check_in') {
          // If there was a previous check-in without checkout, add present till this check-in
          if (lastCheckInMinute !== null) {
            const clampedStart = Math.max(startRange, Math.min(endRange, lastCheckInMinute));
            if (clampedEventMinute > clampedStart) {
              totalMins += clampedEventMinute - clampedStart;
            }
          }
          lastCheckInMinute = eventMinute;
        } else if (event.action === 'check_out') {
          if (lastCheckInMinute !== null) {
            const clampedStart = Math.max(startRange, Math.min(endRange, lastCheckInMinute));
            if (clampedEventMinute > clampedStart) {
              totalMins += clampedEventMinute - clampedStart;
            }
          }
          lastCheckInMinute = null;
        }
      });

      // If still checked in (no checkout after last check-in)
      if (lastCheckInMinute !== null) {
        const clampedStart = Math.max(startRange, Math.min(endRange, lastCheckInMinute));
        let endMinute = endRange;
        if (isDayToday) {
          const nowTz = getTimeInDisplayTimezone(currentTime);
          endMinute = Math.max(startRange, Math.min(endRange, nowTz.hours * 60 + nowTz.minutes));
        }
        if (endMinute > clampedStart) {
          totalMins += endMinute - clampedStart;
        }
      }
    });

    const hours = Math.floor(totalMins / 60);
    const mins = totalMins % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };
  

  // Check if a date is today
  const isToday = (dateKey) => {
    const today = format(new Date(), 'yyyy-MM-dd');
    return dateKey === today;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobileModal}
      TransitionComponent={Fade}
      TransitionProps={{ timeout: 200 }}
      PaperProps={{
        sx: {
          borderRadius: isMobileModal ? 0 : 3,
          overflow: 'hidden',
          bgcolor: '#fafbfc',
          maxHeight: isMobileModal ? '100vh' : '85vh',
        }
      }}
    >
      {/* Header */}
      <Box sx={{ p: { xs: 2, sm: 2.5 }, bgcolor: '#fff', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Stack direction="row" spacing={{ xs: 1.5, sm: 2 }} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
            <Avatar
              src={thumbnail || undefined}
              alt={person_name || 'Unknown person'}
              imgProps={{
                'data-no-image-preview': 'true',
              }}
              onClick={handleProfileImagePreview}
              sx={{
                width: { xs: 44, sm: 56 },
                height: { xs: 44, sm: 56 },
                bgcolor: primary,
                fontWeight: 600,
                fontSize: { xs: '1.1rem', sm: '1.5rem' },
                flexShrink: 0,
                cursor: thumbnail ? 'pointer' : 'default',
              }}
            >
              {avatarLetter}
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    lineHeight: 1.2,
                    fontSize: { xs: '1rem', sm: '1.25rem' },
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    minWidth: 0,
                  }}
                >
                  <EmployeeNameHyperlink
                    text={person_name || 'Unknown Person'}
                    employees={[buildPersonLinkMeta({ person_name, person_id, employee_type }, { routeSource: 'facesPerson' })]}
                    style={{ display: 'inline' }}
                  />
                </Typography>
                <RoleBadgeIcons employeeType={employee_type} size={18} />
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                {employee_code || person_id}
              </Typography>
              <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                <Chip size="small" label={`In: ${total_check_ins}`} sx={{ height: { xs: 20, sm: 24 }, fontSize: { xs: '0.65rem', sm: '0.75rem' }, fontWeight: 600, bgcolor: '#dcfce7', color: '#166534' }} />
                <Chip size="small" label={`Out: ${total_check_outs}`} sx={{ height: { xs: 20, sm: 24 }, fontSize: { xs: '0.65rem', sm: '0.75rem' }, fontWeight: 600, bgcolor: '#fef9c3', color: '#854d0e' }} />
                <Chip size="small" label={`Presence: ${calculateTotalPresence()}`} sx={{ height: { xs: 20, sm: 24 }, fontSize: { xs: '0.65rem', sm: '0.75rem' }, fontWeight: 600, bgcolor: '#dbeafe', color: '#1d4ed8' }} />
                {loading && (
                  <Chip
                    size="small"
                    icon={<CircularProgress size={12} />}
                    label="Loading timeline"
                    sx={{ height: { xs: 20, sm: 24 }, fontSize: { xs: '0.65rem', sm: '0.75rem' }, fontWeight: 600 }}
                  />
                )}
              </Stack>
            </Box>
          </Stack>
          <IconButton onClick={onClose} size="small" sx={{ bgcolor: '#f1f5f9', '&:hover': { bgcolor: '#e2e8f0' }, ml: 1 }}>
            <Close fontSize="small" />
          </IconButton>
        </Stack>
      </Box>

      {/* Timeline Bars List */}
      <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#fafbfc', overflowY: 'auto', maxHeight: isMobileModal ? 'calc(100vh - 180px)' : 'calc(85vh - 200px)' }}>
        {/* Section Header */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          spacing={{ xs: 1, sm: 0 }}
          sx={{ mb: 2.5 }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#374151', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
              Daily Timeline
            </Typography>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#ef4444' }} />
          </Stack>
          {/* Legend - responsive wrap */}
          <Stack direction="row" spacing={{ xs: 1.5, sm: 2 }} flexWrap="wrap" useFlexGap>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Box sx={{ width: { xs: 10, sm: 12 }, height: { xs: 5, sm: 6 }, bgcolor: '#86efac', borderRadius: 0.5 }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.55rem', sm: '0.6rem' } }}>Present</Typography>
            </Stack>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Box sx={{ width: { xs: 10, sm: 12 }, height: { xs: 5, sm: 6 }, bgcolor: '#fecaca', borderRadius: 0.5 }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.55rem', sm: '0.6rem' } }}>Away</Typography>
            </Stack>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Box sx={{ width: { xs: 6, sm: 8 }, height: { xs: 6, sm: 8 }, bgcolor: '#22c55e', borderRadius: '50%' }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.55rem', sm: '0.6rem' } }}>In</Typography>
            </Stack>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Box sx={{ width: { xs: 6, sm: 8 }, height: { xs: 6, sm: 8 }, bgcolor: '#f59e0b', borderRadius: '50%' }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.55rem', sm: '0.6rem' } }}>Out</Typography>
            </Stack>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Box sx={{ width: { xs: 6, sm: 8 }, height: { xs: 6, sm: 8 }, bgcolor: '#64748b', borderRadius: '50%' }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.55rem', sm: '0.6rem' } }}>Missing</Typography>
            </Stack>
          </Stack>
        </Stack>

        {groupedDays.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center', bgcolor: '#fff', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="body2" color="text.secondary">No activity recorded for this period</Typography>
          </Box>
        ) : (
          <Box sx={{ bgcolor: '#fff', borderRadius: 2, border: '1px solid', borderColor: 'divider', p: 2.5 }}>
            {groupedDays.map((day, idx) => (
              <Box key={day.dateKey}>
                <DayTimelineBar
                  date={day.dateLabel}
                  events={day.events}
                  isToday={isToday(day.dateKey)}
                  snapshotCache={snapshotCache}
                  lateEntryTime={lateEntryTrackingEnabled ? lateEntryTime : null}
                  onRequestMarkerImage={onRequestTimelineEventImage}
                />
                {idx < groupedDays.length - 1 && (
                  <Divider sx={{ my: 2, borderColor: '#f1f5f9' }} />
                )}
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* Footer */}
      <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', bgcolor: '#fff', display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="outlined" size="small" onClick={onClose} sx={{ borderRadius: 2, textTransform: 'none', px: 3 }}>
          Close
        </Button>
      </Box>
    </Dialog>
  );
};

// Google Meet-style grid layout - dynamically adjusts based on number of items in each row
export const AttendanceGridLayout = ({
  summaries,
  personThumbnails,
  snapshotCache,
  lateEntryTime,
  lateEntryTrackingEnabled = true,
  onAttendanceEventLogClick,
  onMissingEntrySearchClick = null,
  onLoadAttendanceTimeline = null,
  onRequestAttendanceEventImages = null,
  lazyLoadImages = false,
  onRequestPersonThumbnail = null,
}) => {
  const effectiveLateEntryTime = lateEntryTrackingEnabled ? lateEntryTime : null;
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });

    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  // Responsive: determine cards per row based on container width
  const getCardsPerRow = () => {
    if (containerWidth < 600) return 1;  // Mobile: 1 card
    if (containerWidth < 900) return 2;  // Tablet: 2 cards
    return 3;                             // Desktop: 3 cards
  };

  const cardsPerRow = getCardsPerRow();

  // Group summaries into rows based on responsive cards per row
  const rows = [];
  for (let i = 0; i < summaries.length; i += cardsPerRow) {
    rows.push(summaries.slice(i, i + cardsPerRow));
  }

  // Calculate carousel items based on card width - consistent 3 items per card
  const getCarouselItemsForRow = (itemsInRow) => {
    // Mobile: always 3 items max
    if (containerWidth < 600) {
      return 3;
    }
    // Tablet and Desktop: always 3 items for consistency
    return 3;
  };

  return (
    <Box ref={containerRef}>
      {rows.map((row, rowIndex) => {
        const rowItemCount = row.length;
        const carouselItemsPerView = getCarouselItemsForRow(rowItemCount);
        const isFullWidth = rowItemCount === 1 && cardsPerRow === 1;
        const isHalfWidth = cardsPerRow === 2;
        
        return (
          <Box
            key={`row-${rowIndex}`}
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 2, sm: 2.5 },
              mb: rowIndex < rows.length - 1 ? { xs: 2, sm: 2.5 } : 0,
            }}
          >
            {row.map((summary) => (
              <Box
                key={summary.person_id || summary.person_name || summary.employee_code || 'unknown'}
                sx={{
                  flex: rowItemCount < cardsPerRow ? '0 0 auto' : 1,
                  minWidth: 0,
                  width: rowItemCount < cardsPerRow 
                    ? { xs: '100%', sm: `calc((100% - ${(cardsPerRow - 1) * 20}px) / ${cardsPerRow})` }
                    : 'auto',
                }}
              >
                <AttendanceSummaryCard
                  summary={summary}
                  thumbnail={summary.person_id ? personThumbnails[summary.person_id] : null}
                  snapshotCache={snapshotCache}
                  itemsPerView={carouselItemsPerView}
                  isFullWidth={isFullWidth}
                  isHalfWidth={isHalfWidth}
                  lateEntryTime={effectiveLateEntryTime}
                  lateEntryTrackingEnabled={lateEntryTrackingEnabled}
                  onAttendanceEventLogClick={onAttendanceEventLogClick}
                  onMissingEntrySearchClick={onMissingEntrySearchClick}
                  onLoadAttendanceTimeline={onLoadAttendanceTimeline}
                  onRequestAttendanceEventImages={onRequestAttendanceEventImages}
                  lazyLoadImages={lazyLoadImages}
                  onRequestPersonThumbnail={onRequestPersonThumbnail}
                />
              </Box>
            ))}
          </Box>
        );
      })}
    </Box>
  );
};

const AttendanceSummaryCard = ({
  summary,
  thumbnail,
  snapshotCache,
  itemsPerView = 3,
  isFullWidth = false,
  isHalfWidth = false,
  lateEntryTime = null,
  lateEntryTrackingEnabled = true,
  onAttendanceEventLogClick = null,
  onMissingEntrySearchClick = null,
  onLoadAttendanceTimeline = null,
  onRequestAttendanceEventImages = null,
  lazyLoadImages = false,
  onRequestPersonThumbnail = null,
}) => {
  const { openImagePreview } = useImagePreview();
  const {
    person_name,
    person_id,
    face_ids,
    total_check_ins,
    total_check_outs,
    net_flow,
    recent_events = [],
    last_seen,
    first_seen,
    imbalance_flag,
    employee_code,
    employee_type,
    is_late_entry: isLateEntryFromApi,
  } = summary;

  const [carouselIndex, setCarouselIndex] = useState(0);
  const [timelineOpen, setTimelineOpen] = useState(false);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelineSummary, setTimelineSummary] = useState(summary);
  const ITEMS_PER_VIEW = itemsPerView;
  const timelineEvents = useMemo(() => recent_events.slice(0, 18), [recent_events]); // Allow more events for larger views

  useEffect(() => {
    setTimelineSummary(summary);
  }, [summary]);

  // Reset carousel when items per view changes
  useEffect(() => {
    setCarouselIndex(0);
  }, [itemsPerView]);

  const [isInView, setIsInView] = useState(false);
  const lazyRef = useLazyInView(() => setIsInView(true), { once: true });

  useEffect(() => {
    if (!isInView) return;
    if (person_id && typeof onRequestPersonThumbnail === 'function') {
      onRequestPersonThumbnail(person_id, face_ids);
    }
  }, [isInView, person_id, face_ids, onRequestPersonThumbnail]);

  const totalItems = timelineEvents.length;
  const hasMultiplePages = totalItems > ITEMS_PER_VIEW;
  const maxIndex = Math.max(0, totalItems - ITEMS_PER_VIEW);
  const visibleEvents = useMemo(
    () => timelineEvents.slice(carouselIndex, carouselIndex + ITEMS_PER_VIEW),
    [timelineEvents, carouselIndex, ITEMS_PER_VIEW],
  );

  useEffect(() => {
    if (lazyLoadImages) return;
    if (!isInView) return;
    if (typeof onRequestAttendanceEventImages !== 'function') return;
    const imageEvents = visibleEvents.filter(attendanceLogHasImagePath);
    if (imageEvents.length === 0) return;
    onRequestAttendanceEventImages(imageEvents);
  }, [
    lazyLoadImages,
    isInView,
    visibleEvents,
    onRequestAttendanceEventImages,
  ]);

  const [galleryRevision, setGalleryRevision] = useState(0);
  useEffect(() => {
    const auditIds = visibleEvents.map(getAuditImageKey).filter(Boolean);
    if (auditIds.length === 0) return undefined;
    const unsubs = auditIds.map((auditId) => subscribeAuditImage(auditId, () => {
      setGalleryRevision((prev) => prev + 1);
    }));
    return () => unsubs.forEach((unsub) => unsub());
  }, [visibleEvents]);

  const handleOpenTimeline = async (e) => {
    if (isRecordingPlayInteraction(e)) {
      e?.preventDefault?.();
      e?.stopPropagation?.();
      return;
    }
    setTimelineOpen(true);
    if (typeof onLoadAttendanceTimeline !== 'function') return;
    setTimelineLoading(true);
    try {
      const loadedSummary = await onLoadAttendanceTimeline(summary);
      if (loadedSummary) {
        setTimelineSummary(loadedSummary);
      }
    } finally {
      setTimelineLoading(false);
    }
  };

  const handleCloseTimeline = () => {
    setTimelineOpen(false);
  };

  const avatarLetter = (person_name || person_id || '?').charAt(0).toUpperCase();

  // Real events for In/Out time chips; include Missing Out/In when deciding premises status.
  const realEvents = recent_events.filter((e) => !e?.is_missing_entry);
  const sortedByTime = [...realEvents].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  const firstCheckIn = sortedByTime.find((e) => e.action === 'check_in');
  const firstFlowEvent = sortedByTime.find(
    (e) => e.action === 'check_in' || e.action === 'check_out'
  );
  const lastRealCheckOut = [...sortedByTime].reverse().find((e) => e.action === 'check_out');
  const hasUnsyncedKivoEvent = recent_events.some((evt) => evt?.synced_with_kivo === false);
  // Late chip matches backend: is_late_entry uses earliest DB row for the person (all actions in range) vs threshold (>=).
  const isLateEntry = useMemo(() => {
    if (!lateEntryTrackingEnabled) return false;
    if (typeof isLateEntryFromApi === 'boolean') return isLateEntryFromApi;
    if (!lateEntryTime || !firstFlowEvent) return false;
    try {
      const eventTime = formatDate(firstFlowEvent.created_at, false, false, true);
      return eventTime >= lateEntryTime;
    } catch (e) {
      return false;
    }
  }, [lateEntryTrackingEnabled, isLateEntryFromApi, lateEntryTime, firstFlowEvent]);

  // Premises status: Missing Out (synthetic check_out) counts as out of premises.
  const sortedAllByTime = [...recent_events].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  const mostRecentEvent = sortedAllByTime.length > 0 ? sortedAllByTime[sortedAllByTime.length - 1] : null;
  const isCurrentlyIn = mostRecentEvent?.action === 'check_in';
  // Prefer real checkout for the Out chip; fall back to Missing Out timestamp.
  const lastCheckOut =
    lastRealCheckOut ||
    [...sortedAllByTime].reverse().find((e) => e.action === 'check_out');
  const snapshotFallbackImage = timelineEvents
    .map((evt) => resolveAttendanceEventImage(evt, snapshotCache))
    .find((img) => !!img) || null;
  const primaryImage = thumbnail || snapshotFallbackImage || null;
  const capturedGalleryImages = useMemo(() => {
    void galleryRevision;
    return buildAttendanceCapturedImageGallery({ summary, snapshotCache });
  }, [summary, snapshotCache, galleryRevision]);
  const hasMultipleGalleryImages = capturedGalleryImages.length > 1;

  const handlePrev = (e) => {
    e.stopPropagation();
    setCarouselIndex((prev) => Math.max(0, prev - ITEMS_PER_VIEW));
  };

  const handleNext = (e) => {
    e.stopPropagation();
    setCarouselIndex((prev) => Math.min(maxIndex, prev + ITEMS_PER_VIEW));
  };

  const handleEventPhotoClick = (evt, e) => {
    e.stopPropagation();
    if (evt?.is_missing_entry) return;
    const recognitionLog = evt?.recognition_audit_log;
    if (!recognitionLog || !onAttendanceEventLogClick) return;
    const eventImage = evt?.is_manual ? ANONYMOUS_IMAGE : (resolveAttendanceEventImage(evt, snapshotCache) || primaryImage || ANONYMOUS_IMAGE);
    onAttendanceEventLogClick({
      log: recognitionLog,
      image: eventImage,
      enrolledFaceIds: Array.isArray(face_ids) ? face_ids : [],
    });
  };

  const handleViewImagesClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const firstImage = capturedGalleryImages[0];
    if (!firstImage) return;
    openImagePreview({
      src: firstImage.src,
      title: firstImage.title || 'Captured frame',
      alt: firstImage.alt || firstImage.title || 'Captured frame',
      images: capturedGalleryImages,
      index: 0,
      employeeName: person_name || 'Unknown person',
      employeeId: employee_code || person_id || '',
      employeeImage: thumbnail || '',
    });
  };

  return (
    <>
    <Card
      ref={lazyRef}
      variant="outlined"
      onClick={handleOpenTimeline}
      sx={{
        borderRadius: 3,
        width: '100%',
       
        color: '#fff',
        borderTop: `3px solid ${hasUnsyncedKivoEvent ? '#dc2626' : imbalance_flag ? '#f6ad55' : primary}`,
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 10px 30px rgba(15, 23, 42, 0.21)',
        cursor: 'pointer',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          boxShadow: '0 20px 40px rgba(15,23,42,0.15)',
          transform: 'translateY(-4px)',
          borderColor: hasUnsyncedKivoEvent ? '#dc2626' : primary,
        },
        '&:active': {
          transform: 'translateY(-2px)',
        },
      }}
    >
      <CardContent sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: { xs: 1.25, sm: 1.5, md: isFullWidth ? 2 : 1.5 },
        flex: 1,
        p: { xs: 1.5, sm: 2, md: isFullWidth ? 3 : 2 }
      }}>
        {/* Header: Avatar + Name/ID */}
        <Stack direction="row" spacing={{ xs: 1.25, sm: 1.5 }} alignItems="center" sx={{ minWidth: 0 }}>
          <Avatar
            src={primaryImage || undefined}
            alt={person_name || 'Unknown person'}
            sx={{
              width: { xs: 40, sm: 48, md: isFullWidth ? 64 : 48 },
              height: { xs: 40, sm: 48, md: isFullWidth ? 64 : 48 },
              fontWeight: 600,
              bgcolor: '#EEF2FF',
              color: '#374151',
              flexShrink: 0,
              fontSize: { xs: '0.9rem', sm: '1rem', md: isFullWidth ? '1.25rem' : '1rem' },
            }}
          >
            {avatarLetter}
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="subtitle1"
              color="text.primary"
              sx={{
                fontWeight: 600,
                fontSize: { xs: '0.85rem', sm: '0.9rem', md: isFullWidth ? '1rem' : '0.9rem' }
              }}
              noWrap
            >
              <Stack direction="row" alignItems="center" justifyContent={'space-between'} spacing={0.75}>
                <EmployeeNameHyperlink
                  text={person_name || 'Unknown person'}
                  employees={[buildPersonLinkMeta({ person_name, person_id, employee_type }, { routeSource: 'facesPerson' })]}
                  style={{ display: 'inline' }}
                />
                <RoleBadgeIcons employeeType={employee_type} size={16} />
{isLateEntry && employee_code && String(employee_code).trim() !== '' && (
                    <Chip
                    size="small"
                    label="Late Entry"
                    sx={{
                      height: { xs: 22, sm: 24 },
                      fontSize: { xs: '0.65rem', sm: '0.75rem' },
                      fontWeight: 600,
                      bgcolor: '#fff',
                      color: (theme) => theme.palette.warning.main
                    }}
                  />
            
                )}
              </Stack>
            </Typography>
            <Stack direction="row" alignItems="center" justifyContent={'space-between'}>
              <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                {employee_code}
              </Typography>
              <Chip
                size="small"
                icon={isCurrentlyIn ? <Login sx={{ fontSize: 14 }} /> : <Logout sx={{ fontSize: 14 }} />}
                label={isCurrentlyIn ? 'In Premises' : 'Out of Premises'}
                sx={{
                  height: { xs: 22, sm: 24 },
                  fontSize: { xs: '0.65rem', sm: '0.70rem' },
                  bgcolor: isCurrentlyIn ? '#dcfce7' : '#fef3c7',
                  color: isCurrentlyIn ? '#166534' : '#92400e',
                  fontWeight: 600,
                  '& .MuiChip-icon': {
                    color: 'inherit',
                  },
                }}
              />
            </Stack>
          </Box>
        </Stack>

        {/* Stats Row: First In / Last Out / Status */}
        <Stack direction="row" spacing={0.5} flexWrap="wrap" alignItems="center" useFlexGap>
          {firstCheckIn && (
            <Chip 
              size="small" 
              label={`In: ${formatDate(firstCheckIn.created_at, true, false)}`} 
              color="success" 
              variant="outlined" 
              sx={{ height: { xs: 22, sm: 24 }, fontSize: { xs: '0.65rem', sm: '0.75rem' } }} 
            /> 
          )}
          {lastCheckOut && (
            <Chip 
              size="small" 
              label={`Out: ${formatDate(lastCheckOut.created_at, true, false)}`} 
              color="error" 
              variant="outlined" 
              sx={{ height: { xs: 22, sm: 24 }, fontSize: { xs: '0.65rem', sm: '0.75rem' } }} 
            />
          )}
          {hasMultipleGalleryImages && (
            <Tooltip title="Captured Images" arrow>
              <IconButton
                size="small"
                aria-label="Open captured image gallery"
                onClick={handleViewImagesClick}
                sx={{
                  ml: 'auto',
                  width: { xs: 28, sm: 30 },
                  height: { xs: 28, sm: 30 },
                  bgcolor: '#fff',
                  color: primary,
                  border: '1px solid rgba(64, 81, 138, 0.24)',
                  boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
                  '&:hover': {
                    bgcolor: '#f8fafc',
                    borderColor: primary,
                  },
                }}
              >
                <PhotoLibraryIcon sx={{ fontSize: { xs: 17, sm: 18 } }} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>

        <Divider />

        <Box>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Recent activity
            </Typography>
            {/* Always render pagination controls to maintain consistent height, but hide when not needed */}
            <Stack 
              direction="row" 
              spacing={0.5} 
              alignItems="center"
              sx={{ visibility: hasMultiplePages ? 'visible' : 'hidden' }}
            >
              <IconButton 
                size="small" 
                onClick={handlePrev} 
                disabled={carouselIndex === 0}
                sx={{ 
                  width: 28, 
                  height: 28,
                  bgcolor: carouselIndex === 0 ? 'transparent' : 'action.hover',
                  '&:hover': { bgcolor: 'action.selected' },
                }}
              >
                <ChevronLeft fontSize="small" />
              </IconButton>
              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 40, textAlign: 'center' }}>
                {Math.floor(carouselIndex / ITEMS_PER_VIEW) + 1}/{Math.ceil(totalItems / ITEMS_PER_VIEW)}
              </Typography>
              <IconButton 
                size="small" 
                onClick={handleNext} 
                disabled={carouselIndex >= maxIndex}
                sx={{ 
                  width: 28, 
                  height: 28,
                  bgcolor: carouselIndex >= maxIndex ? 'transparent' : 'action.hover',
                  '&:hover': { bgcolor: 'action.selected' },
                }}
              >
                <ChevronRight fontSize="small" />
              </IconButton>
            </Stack>
          </Stack>
          {timelineEvents.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              No attendance logs in this range.
            </Typography>
          ) : (
            <Box sx={{ overflow: 'hidden', width: '100%' }}>
              <Box
                sx={{
                  display: 'flex',
                  gap: 1.2,
                }}
              >
                {visibleEvents.map((evt) => (
                  <Box
                    key={evt.attendance_id || `${evt.action}-${evt.created_at}`}
                    sx={{
                      flex: `0 0 calc(${100 / ITEMS_PER_VIEW}% - ${(ITEMS_PER_VIEW - 1) * 9.6 / ITEMS_PER_VIEW}px)`,
                      minWidth: 90,
                      maxWidth: 180,
                    }}
                  >
                    <AttendanceEventTile
                      event={evt}
                      image={
                        lazyLoadImages && getAuditImageKey(evt)
                          ? undefined
                          : resolveAttendanceEventImage(evt, snapshotCache)
                      }
                      fallbackImage={primaryImage}
                      fallbackLetter={avatarLetter}
                      lazyLoadImages={lazyLoadImages}
                      onRequestAttendanceEventImages={onRequestAttendanceEventImages}
                      onImageClick={(e) => handleEventPhotoClick(evt, e)}
                      isLogClickable={!!evt?.recognition_audit_log}
                      onMissingEntrySearchClick={onMissingEntrySearchClick ? (missingEvent) => {
                        onMissingEntrySearchClick(missingEvent, primaryImage || thumbnail, recent_events, person_id);
                      } : null}
                    />
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
    
    {/* Timeline Modal */}
    <TimelineModal
      open={timelineOpen}
      onClose={handleCloseTimeline}
      summary={timelineSummary}
      loading={timelineLoading}
      thumbnail={thumbnail}
      snapshotCache={snapshotCache}
      lateEntryTime={lateEntryTime}
      lateEntryTrackingEnabled={lateEntryTrackingEnabled}
      onRequestTimelineEventImage={(event) => {
        if (typeof onRequestAttendanceEventImages !== 'function' || !event) return;
        onRequestAttendanceEventImages([event]);
      }}
    />
    </>
  );
};

const AttendanceEventTile = ({
  event,
  image,
  fallbackImage,
  fallbackLetter,
  lazyLoadImages = false,
  onRequestAttendanceEventImages = null,
  onImageClick = null,
  isLogClickable = false,
  onMissingEntrySearchClick = null,
}) => {
  const isCheckIn = event.action === 'check_in';
  const isMissingEntry = !!event.is_missing_entry;
  const isUnsyncedWithKivo = event?.synced_with_kivo === false;
  const missingLabel = isCheckIn ? 'Missing In' : 'Missing Out';
  const actionLabel = isMissingEntry ? missingLabel : (isCheckIn ? 'IN' : 'OUT');
  const rawPlaybackLog = event?.recognition_audit_log && typeof event.recognition_audit_log === 'object'
    ? event.recognition_audit_log
    : null;
  const playbackAuditId =
    rawPlaybackLog?.audit_id ||
    event?.snapshot_audit_id ||
    event?.audit_id ||
    null;
  const playbackLog = playbackAuditId
    ? {
        ...(rawPlaybackLog || {}),
        audit_id: playbackAuditId,
        recording_ready:
          rawPlaybackLog?.recording_ready ??
          event?.recording_ready ??
          0,
      }
    : null;
  const hasStoredAttendanceImage = attendanceLogHasImagePath(event);
  const auditImageId = getAuditImageKey(event);
  const useLazyAuditImage = lazyLoadImages && hasStoredAttendanceImage && Boolean(auditImageId);
  const needsAttendanceOnlyLazy =
    lazyLoadImages && hasStoredAttendanceImage && !auditImageId && Boolean(event?.image_available);
  const { ref: lazyAuditRef, url: lazyAuditUrl } = useAuditImage(auditImageId, {
    enabled: useLazyAuditImage,
    rootMargin: '0px',
  });
  const { ref: attendanceLazyRef, inView: attendanceLazyInView } = useInView({
    triggerOnce: true,
    rootMargin: '200px',
    skip: !needsAttendanceOnlyLazy,
  });
  useEffect(() => {
    if (!needsAttendanceOnlyLazy || !attendanceLazyInView) return;
    if (typeof onRequestAttendanceEventImages !== 'function') return;
    onRequestAttendanceEventImages([event]);
  }, [needsAttendanceOnlyLazy, attendanceLazyInView, event, onRequestAttendanceEventImages]);
  const imageContainerRef = useCallback(
    (node) => {
      if (useLazyAuditImage) lazyAuditRef(node);
      if (needsAttendanceOnlyLazy) attendanceLazyRef(node);
    },
    [useLazyAuditImage, needsAttendanceOnlyLazy, lazyAuditRef, attendanceLazyRef],
  );
  const expectedPlaybackCamera = isCheckIn ? 'IN' : 'OUT';
  const playbackCamera = String(playbackLog?.camera || '').trim().toUpperCase();
  const playbackCameraIsLane = playbackCamera === 'IN' || playbackCamera === 'OUT';
  const playbackCameraMatchesLane = !playbackCameraIsLane || playbackCamera === expectedPlaybackCamera;
  const recordingReadyFromApi = isAuditRecordingReady(playbackLog);
  const canShowRecordingPlay =
    !isMissingEntry &&
    !event?.is_manual &&
    playbackCameraMatchesLane &&
    Boolean(playbackLog?.audit_id) &&
    recordingReadyFromApi;
  const displayImage = (() => {
    if (!hasStoredAttendanceImage) return ANONYMOUS_IMAGE;
    if (useLazyAuditImage) {
      if (lazyAuditUrl === undefined) return fallbackImage || ANONYMOUS_IMAGE;
      return lazyAuditUrl || ANONYMOUS_IMAGE;
    }
    if (isValidSnapshotImageUrl(image)) return image;
    if (image === null) return ANONYMOUS_IMAGE;
    if (image === undefined) return fallbackImage || null;
    return image || fallbackImage || ANONYMOUS_IMAGE;
  })();
  const showEventTime = !isMissingEntry;

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
      <Paper
        variant="outlined"
        sx={{
          borderRadius: { xs: 1.5, sm: 2 },
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          borderStyle: isMissingEntry ? 'dashed' : 'solid',
          borderColor: isMissingEntry ? '#94a3b8' : isUnsyncedWithKivo ? '#dc2626' : undefined,
          bgcolor: isMissingEntry ? '#f8fafc' : undefined,
        }}
      >
        <Box
          ref={lazyLoadImages ? imageContainerRef : undefined}
          sx={{ position: 'relative', height: { xs: 80, sm: 100, md: 110 }, background: '#0f172a', flexShrink: 0 }}
        >
          {displayImage ? (
            <Box
              component="img"
              src={displayImage}
              alt={actionLabel}
              onClick={isMissingEntry ? undefined : onImageClick}
              sx={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                cursor: (isLogClickable && !isMissingEntry) ? 'pointer' : 'default',
                opacity: isMissingEntry ? 0.45 : 1,
              }}
            />
          ) : null}
          <AuditRecordingPlayButton
            log={playbackLog}
            enabled={canShowRecordingPlay}
            placement="top"
            buttonSize={28}
            iconSize={18}
            anchorSx={{ top: 6, right: 6 }}
          />
          {isMissingEntry && onMissingEntrySearchClick && (
            <Tooltip title="Search Audit Logs for Missing Entry" arrow placement="top">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onMissingEntrySearchClick(event);
                }}
                sx={{
                  position: 'absolute',
                  top: 6,
                  left: 6,
                  zIndex: 2,
                  bgcolor: 'rgba(255,255,255,0.92)',
                  '&:hover': { bgcolor: '#fff' },
                  width: 30,
                  height: 30,
                }}
              >
                <SearchIcon sx={{ fontSize: 16, color: primary }} />
              </IconButton>
            </Tooltip>
          )}
          {
            !isMissingEntry && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 4,
                  left: 4,
                  zIndex: 1,
                  height: { xs: 18, sm: 20 },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  px: { xs: .5, sm: .6 },
                  fontWeight: 700,
                  fontSize: { xs: '0.58rem', sm: '0.66rem' },
                  letterSpacing: 0.3,
                  bgcolor: isMissingEntry
                    ? '#e2e8f0CF'
                    : isCheckIn
                    ? '#dcfce7CF'
                    : '#fee2e2CF',

                  color: isMissingEntry
                    ? '#334155DF'
                    : isCheckIn
                    ? '#166534DF'
                    : '#991b1bDF',

                  borderRadius: { xs: 1.5, sm: 2 },
                  whiteSpace: 'nowrap',
                }}
              >
                {actionLabel}
              </Box>
            )
          }
        </Box>
        <Box
          sx={{
            p: { xs: 0.75, sm: 1 },
            display: 'flex',
            flexDirection: isMissingEntry ? 'column' : 'row',
            justifyContent: 'space-between',
            alignItems: isMissingEntry ? 'flex-start' : 'center',
            gap: isMissingEntry ? 0.75 : 0.5,
          }}
        >
          <Box sx={{ minWidth: 0, flex: isMissingEntry ? 'none' : 1, width: '100%' }}>
            <Typography variant="body2" noWrap={!isMissingEntry} sx={{ fontWeight: 600, fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem' }, lineHeight: 1.3 }}>
              {showEventTime ? formatDate(event.created_at, true, false) : missingLabel}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap={!isMissingEntry} sx={{ fontSize: { xs: '0.6rem', sm: '0.65rem', md: '0.7rem' }, lineHeight: 1.2 }}>
              {showEventTime ? formatDateOnly(event.created_at) : '--:--:--'}
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};
